import axios, { AxiosError, Canceler } from 'axios';
import { axiosGet, axiosPost, axiosPut } from 'utilities/axiosManagerFunctions';
import { IMyOpenRolesResponseData } from 'modules/hiring/models/myOpenRequisition.model';
import { eeEnvVariables } from 'utilities/constants';
import {
  IReqCandidatesPayload,
  IReqCandidatesResponse,
  IReqCandidatesContactDetails,
  ICandidatesContactDetailsPayload,
} from 'modules/hiring/models/IRequisitionCandidates.interface';
import {
  IInterviewerPanelPayload,
  IInterviewersPanelResponse,
  TCandidatePipelineResponse,
} from 'modules/hiring/models/candidatePipeline.model';
import {
  ICandidateInterviewFeedbackInputParams,
  ICandidateProfileApplicationResponse,
  ICandidateSkillResponse,
  ICandidateWorkHistoryResponse,
  ICandidateResumeResponse,
  ICandidateResumeDownloadResponse,
  IRejectionReasonResponse,
  ICandidateWorkHistoryPayload,
  ICandidateSkillPayload,
  ICandidateAssessmentReportPayload,
  ICandidateAssessmentResponse,
  ICandidateDynamicReportResponse,
  IKfasConfigResponse,
  IAssessmentStatusResponse,
} from 'modules/hiring/models/candidateProfile.model';
import {
  ICandidateAwaitingPayload,
  ICandidateAwaitingReviewData,
} from 'modules/hiring/models/candidateAwaitingReview.model';
import { IHiringMakeDecision } from 'modules/hiring/components/decisionModal/makeDecisionModal';
import { IRecentHiringActivityResponse } from 'modules/hiring/models/recentHiringActivity.model';
import { IHiringManagerDetailsResponse } from 'modules/hiring/models/hiringManagerDetails.model';
import { ICandidateFeedbackListResponse } from '../models/candidateFeedbackListDetails.model';
import { MOCK_CANDIDATE_FEEDBACK_DETAILED_LIST } from '__mocks__/modules/hiring/candidateFeedbackDetailList.mock';
import { ICandidateInterviewFeedbackOverallRating } from '../models/candidateFeedbackOverallRating.model';
import { ICandidateEducationResponse } from '../models/candidateProfile.model';
import { ITokenResponse } from 'models/interfaces/auth';

const getAPIError = (err: any) => {
  /* istanbul ignore next */
  const errorMessage = err.message || err.toString();
  return new AxiosError(
    `API Error: "${errorMessage}"`,
    undefined,
    undefined,
    undefined,
    err?.response
  );
};

export const axiosGetHiringManagerDetails =
  async (): Promise<IHiringManagerDetailsResponse> => {
    try {
      const response = await axiosGet<IHiringManagerDetailsResponse>(
        `Employee/GetHiringManagerDetails`,
        undefined,
        eeEnvVariables.HM_API_URL
      );
      return response.data;
    } catch (err: any) {
      throw getAPIError(err);
    }
  };

let hiringManagerDetails: IHiringManagerDetailsResponse | undefined;
let hiringManagerDetailsPromise:
  | Promise<IHiringManagerDetailsResponse>
  | undefined;

export const getHiringManagerDetails = async () => {
  if (hiringManagerDetails) {
    return hiringManagerDetails;
  } else {
    /* istanbul ignore if */
    if (hiringManagerDetailsPromise) {
      return hiringManagerDetailsPromise;
    } else {
      hiringManagerDetailsPromise = axiosGetHiringManagerDetails();
      hiringManagerDetails = await hiringManagerDetailsPromise;
      hiringManagerDetailsPromise = undefined;
      return hiringManagerDetails;
    }
  }
};

export const axiosRequisitionTile =
  async (): Promise<IMyOpenRolesResponseData> => {
    try {
      const { nimbleOrgId } = await getHiringManagerDetails();
      const payload = {
        isAscending: true,
        sortName: '',
        pageRecordsCount: 1000,
        pageIndex: 1,
        searchKeywords: '',
        hiringManagerName: [],
        recruiterName: [],
        location: [],
        status: [],
        lifecycle_status: [],
        cycle_Type: [],
        startDate: null,
        endDate: null,
        organizationId: nimbleOrgId,
      };

      const response = await axiosPost<any, IMyOpenRolesResponseData>(
        `Requisition`,
        payload,
        undefined,
        eeEnvVariables.HM_API_URL
      );

      return response.data;
    } catch (err: any) {
      throw getAPIError(err);
    }
  };

export const axiosRequisitionCandidates = async (
  reqID: string | undefined,
  payload: IReqCandidatesPayload,
  onInit?: (canceler: Canceler) => void
): Promise<IReqCandidatesResponse> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();

    const response = await axiosPost<
      IReqCandidatesPayload & { organizationId: number },
      IReqCandidatesResponse
    >(
      `Candidate/GetHMCandidateList${reqID ? `?requisitionID=${reqID}` : ''}`,
      {
        ...payload,
        organizationId: nimbleOrgId,
      },
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCandidateContactDetails = async (
  payload: ICandidatesContactDetailsPayload,
  onInit?: (canceler: Canceler) => void
): Promise<IReqCandidatesContactDetails> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();
    const response = await axiosPost<
      ICandidatesContactDetailsPayload,
      IReqCandidatesContactDetails
    >(
      `Candidate/GetCandidateContactDetail`,
      {
        ...payload,
        orgId: nimbleOrgId,
      },
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCandidatesPipeline = async (
  reqID: string | undefined,
  payload: Omit<IReqCandidatesPayload, 'organizationId'>,
  onInit?: (canceler: Canceler) => void
): Promise<TCandidatePipelineResponse> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();
    const response = await axiosPost<
      IReqCandidatesPayload & { organizationId: number },
      TCandidatePipelineResponse
    >(
      `Candidate/GetCandidatePipelineDetails${reqID ? `?requisitionID=${reqID}` : ''
      }`,
      {
        ...payload,
        organizationId: nimbleOrgId,
      },
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCandidatesApplication = async (
  candidateId: number | undefined,
  requisitionID: string | undefined
): Promise<ICandidateProfileApplicationResponse> => {
  try {
    const response = await axiosGet<ICandidateProfileApplicationResponse>(
      `Candidate/GetCandidateApplicationsDetail?candidateId=${candidateId}&requisitionID=${requisitionID}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetKfasConfig = async (
  orgId: number
): Promise<IKfasConfigResponse> => {
  try {
    const response = await axiosGet<IKfasConfigResponse>(
      `Integration/GetKfasConfig?orgId=${orgId}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetAssessmentStatus = async (
  kfasClientId: number | undefined,
  participantId?: number | null,
  kfasProjectId?: number | null,
  onInit?: (canceler: Canceler) => void
): Promise<IAssessmentStatusResponse> => {
  try {
    const response = await axiosPost<IAssessmentStatusResponse>(
      `Integration/AssessmentStatus?kfasClientId=${kfasClientId}&participantId=${participantId}&kfasProjectId=${kfasProjectId}`,
      undefined,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCandidatesAwaitingReview = async (
  payload: ICandidateAwaitingPayload,
  onInit?: (canceler: Canceler) => void
): Promise<ICandidateAwaitingReviewData> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();

    const response = await axiosPost<
      ICandidateAwaitingPayload & { organizationId: number },
      ICandidateAwaitingReviewData
    >(
      `Candidate/GetCandidateAwaitList`,
      {
        ...payload,
        organizationId: nimbleOrgId,
      },
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosMakeHiringDecision = async (
  payload: IHiringMakeDecision,
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `Candidate/MakeHiringDecision`,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCandidateEducation = async (
  candidateId: number | undefined,
  orgId: number | undefined
): Promise<ICandidateEducationResponse> => {
  try {
    const response = await axiosGet<ICandidateEducationResponse>(
      `Candidate/GetCandidateCertificationDetails?candidateId=${candidateId}&orgId=${orgId}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCandidateSkills = async (
  payload: ICandidateSkillPayload,
  onInit?: (canceler: Canceler) => void
): Promise<ICandidateSkillResponse> => {
  try {
    const response = await axiosPost<
      ICandidateSkillPayload,
      ICandidateSkillResponse
    >(
      `Candidate/GetCandidateSkills`,
      {
        ...payload,
      },
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

// get Work History Details
export const axiosGetWorkHistoryDetails = async (
  payload: ICandidateWorkHistoryPayload,
  onInit?: (canceler: Canceler) => void
): Promise<ICandidateWorkHistoryResponse> => {
  try {
    const response = await axiosPost<
      ICandidateWorkHistoryPayload,
      ICandidateWorkHistoryResponse
    >(
      `Candidate/GetCandidateWorkHistory`,
      {
        ...payload,
      },
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response?.data;
  } catch (err: any) {
    const errorMessage = err.message || err.toString();
    throw new Error(`API Error: "${errorMessage}"`);
  }
};

// get Assessment Report Details
export const axiosGetDownloadAssessmentReportDetails = async (
  payload: ICandidateAssessmentReportPayload,
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<ICandidateAssessmentReportPayload>(
      `Integration/CandidateAssessmentReport`,
      {
        ...payload,
      },
      { responseType: 'blob' },
      eeEnvVariables.HM_API_URL
    );
    return response;
  } catch (err: any) {
    const errorMessage = err.message || err.toString();
    throw new Error(`API Error: "${errorMessage}"`);
  }
};

// get Assessment Report Dynamic Details
export const axiosGetDownloadDynamicReportDetails = async (
  candidateID: number | undefined,
  kfasProjectSuccessProfileId: number | undefined,
  kfas_Client_Id: number | undefined,
  kfasProjectId: number | undefined
): Promise<ICandidateDynamicReportResponse> => {
  try {
    const response = await axiosGet<ICandidateDynamicReportResponse>(
      `Integration/GetKFasDynamicReports?candidateId=${candidateID}&successProfileId=${kfasProjectSuccessProfileId}&kfasClientId=${kfas_Client_Id}&projectId=${kfasProjectId}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response?.data;
  } catch (err: any) {
    const errorMessage = err.message || err.toString();
    throw new Error(`API Error: "${errorMessage}"`);
  }
};

export const axiosGetRejectReasonList =
  async (): Promise<IRejectionReasonResponse> => {
    try {
      const response = await axiosGet<IRejectionReasonResponse>(
        `RejectionReason/GetRejectionReasons`,
        undefined,
        eeEnvVariables.HM_API_URL
      );
      return response.data;
    } catch (err: any) {
      throw getAPIError(err);
    }
  };

export const axiosRecentHiringActivity =
  async (): Promise<IRecentHiringActivityResponse> => {
    try {
      const response = await axiosGet<IRecentHiringActivityResponse>(
        `Candidate/GetRecentHiringActivity`,
        undefined,
        eeEnvVariables.HM_API_URL
      );
      return response.data;
    } catch (err: any) {
      throw getAPIError(err);
    }
  };

export const axiosCandidatesResume = async (
  candidateId: number | undefined
): Promise<ICandidateResumeResponse> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();
    const response = await axiosGet<ICandidateResumeResponse>(
      `Candidate/GetCandidateResumeDetails?candidateId=${candidateId}&orgId=${nimbleOrgId}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCandidatesResumeDownload = async (
  fileName: string
): Promise<ICandidateResumeDownloadResponse> => {
  try {
    const response = await axiosGet<ICandidateResumeDownloadResponse>(
      `Candidate/GetDownloadCandidateResumeDetails?fileName=${fileName}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetInterviewPanel = async (
  requisitionId: string
): Promise<IInterviewersPanelResponse> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();
    const response = await axiosGet<IInterviewersPanelResponse>(
      `InterviewPanel/GetInterviewPanel?requisitionId=${requisitionId}&organizationId=${nimbleOrgId}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCreateInterviewPanel = async (
  payload: IInterviewerPanelPayload,
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();
    const _payload = { ...payload, organizationId: nimbleOrgId };
    const response = await axiosPost<any>(
      `InterviewPanel/CreateInterviewPanel`,
      _payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    getAPIError(err);
  }
};

export const axiosUpdateInterview = async (
  payload: IInterviewerPanelPayload,
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const { nimbleOrgId } = await getHiringManagerDetails();
    const _payload = { ...payload, organizationId: nimbleOrgId };
    const response = await axiosPut<any>(
      `InterviewPanel/UpdateInterviewPanelList`,
      _payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        }),
      },
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    getAPIError(err);
  }
};

export const axiosGetInterviewFeedbackList = async (
  requisitionID: number | undefined,
  candidateID: number | undefined
): Promise<ICandidateFeedbackListResponse> => {
  const { nimbleOrgId } = await getHiringManagerDetails();
  try {
    const response = await axiosGet<ICandidateFeedbackListResponse>(
      `InterviewFeedback/GetAllInterviewersFeedback?requisitionId=${requisitionID}&candidateId=${candidateID}&organizationId=${nimbleOrgId}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetCandidateOverallRating = async ({
  requisitionID,
  candidateID,
}: ICandidateInterviewFeedbackInputParams): Promise<ICandidateInterviewFeedbackOverallRating> => {
  const { nimbleOrgId } = await getHiringManagerDetails();
  try {
    const response = await axiosGet<ICandidateInterviewFeedbackOverallRating>(
      `InterviewFeedback/GetAggregateInterviewersFeedback?requisitionId=${requisitionID}&candidateId=${candidateID}&organizationId=${nimbleOrgId}`,
      undefined,
      eeEnvVariables.HM_API_URL
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};


export const axiosGetIDPConfig = async (
  jwtToken: string
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `OpenAPI/GetIDPConfig?jwt_token=${jwtToken}`,
      undefined,
      undefined,
      eeEnvVariables.HM_OPEN_API_URL,
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};